import { supabase, isSupabaseAvailable } from '../lib/supabase';

/**
 * RAG (Retrieval Augmented Generation) Service
 * 
 * Handles knowledge base management and query processing for cemetery-specific AI responses.
 * This service manages data preparation and calls our secure API endpoints for OpenAI integration.
 * 
 * Key Features:
 * - Knowledge chunk storage and retrieval
 * - Semantic search preparation
 * - API endpoint integration for secure OpenAI calls
 * - Cemetery-specific knowledge base management
 */

export interface KnowledgeChunk {
  id: string;
  cemetery_id: string;
  content: string;
  embedding?: number[];
  metadata?: {
    source?: string;
    type?: 'policy' | 'service' | 'location' | 'general';
    category?: string;
    last_updated?: string;
    [key: string]: any;
  };
  created_at: string;
  updated_at: string;
}

export interface RAGQueryRequest {
  query: string;
  cemetery_id: string;
  context_limit?: number;
  similarity_threshold?: number;
}

export interface RAGQueryResponse {
  response: string;
  sources?: KnowledgeChunk[];
  context_used?: string;
  error?: string;
}

export interface EmbeddingRequest {
  text: string;
  cemetery_id: string;
}

export interface EmbeddingResponse {
  embedding: number[];
  error?: string;
}

/**
 * RAG Service Class
 * Manages knowledge base operations and API interactions
 */
export class RAGService {
  private static instance: RAGService;
  private cache = new Map<string, KnowledgeChunk[]>();
  private cacheExpiry = new Map<string, number>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  public static getInstance(): RAGService {
    if (!RAGService.instance) {
      RAGService.instance = new RAGService();
    }
    return RAGService.instance;
  }

  /**
   * Query the knowledge base for relevant information
   * Uses semantic search with embeddings to find relevant knowledge chunks
   */
  async queryKnowledgeBase(request: RAGQueryRequest): Promise<RAGQueryResponse> {
    try {
      console.log('🔍 RAG Service: Querying knowledge base for cemetery:', request.cemetery_id);

      if (!isSupabaseAvailable()) {
        throw new Error('Supabase not available');
      }

      // Step 1: Generate embedding for the search query
      console.log('🔢 Generating embedding for query:', request.query);
      const embeddingResponse = await this.generateEmbedding({
        text: request.query,
        cemetery_id: request.cemetery_id
      });

      if (embeddingResponse.error || !embeddingResponse.embedding || embeddingResponse.embedding.length === 0) {
        console.warn('⚠️ Failed to generate embedding, falling back to text search');
        // Fallback to text-based search
        return await this.fallbackTextSearch(request);
      }

      console.log('✅ Embedding generated successfully, dimensions:', embeddingResponse.embedding.length);

      // Step 2: Call the match_knowledge_chunks database function
      console.log('🔍 Calling match_knowledge_chunks RPC function');
      const { data: matchResults, error: rpcError } = await supabase!
        .rpc('match_knowledge_chunks', {
          query_embedding: embeddingResponse.embedding,
          cemetery_id_param: request.cemetery_id,
          match_threshold: request.similarity_threshold || 0.7,
          match_count: 10
        });

      if (rpcError) {
        console.error('❌ RPC function failed:', rpcError);
        // Fallback to text search if RPC fails
        console.log('🔄 Falling back to text search');
        return await this.fallbackTextSearch(request);
      }

      if (!matchResults || matchResults.length === 0) {
        console.log('ℹ️ No semantic matches found, trying text search fallback');
        return await this.fallbackTextSearch(request);
      }

      console.log(`✅ Found ${matchResults.length} semantic matches`);

      // Step 3: Format results and build context
      const sources: KnowledgeChunk[] = matchResults.map((result: any) => ({
        id: result.id,
        cemetery_id: result.cemetery_id,
        content: result.content,
        embedding: result.embedding,
        metadata: {
          ...result.metadata,
          similarity_score: result.similarity
        },
        created_at: result.created_at,
        updated_at: result.updated_at
      }));

      // Build context from top matches
      const contextLimit = request.context_limit || 3000;
      let context = '';
      let usedChars = 0;

      for (const source of sources) {
        const chunkText = `${source.content}\n\n`;
        if (usedChars + chunkText.length <= contextLimit) {
          context += chunkText;
          usedChars += chunkText.length;
        } else {
          break;
        }
      }

      // Step 4: Generate AI response using the context
      const aiResponse = await this.generateContextualResponse(request.query, context, request.cemetery_id);

      console.log('✅ RAG Service: Semantic query completed successfully');
      
      return {
        response: aiResponse,
        sources,
        context_used: context.trim()
      };

    } catch (error) {
      console.error('❌ RAG Service: Query failed:', error);
      return {
        response: 'I apologize, but I\'m having trouble accessing my knowledge base right now. Please contact our staff directly for assistance.',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Fallback text search when semantic search is unavailable
   */
  private async fallbackTextSearch(request: RAGQueryRequest): Promise<RAGQueryResponse> {
    try {
      console.log('🔍 RAG Service: Using fallback text search');
      
      const chunks = await this.searchKnowledgeChunks(
        request.cemetery_id,
        request.query,
        5
      );

      if (chunks.length === 0) {
        return {
          response: 'I couldn\'t find specific information about that topic. Please contact our staff for more detailed assistance.',
          sources: [],
          context_used: ''
        };
      }

      // Build context from text search results
      const contextLimit = request.context_limit || 3000;
      let context = '';
      let usedChars = 0;

      for (const chunk of chunks) {
        const chunkText = `${chunk.content}\n\n`;
        if (usedChars + chunkText.length <= contextLimit) {
          context += chunkText;
          usedChars += chunkText.length;
        } else {
          break;
        }
      }

      const aiResponse = await this.generateContextualResponse(request.query, context, request.cemetery_id);

      return {
        response: aiResponse,
        sources: chunks,
        context_used: context.trim()
      };
    } catch (error) {
      console.error('❌ RAG Service: Fallback text search failed:', error);
      return {
        response: 'I apologize, but I\'m having trouble accessing my knowledge base right now. Please contact our staff directly for assistance.',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Generate contextual AI response using retrieved knowledge
   */
  private async generateContextualResponse(query: string, context: string, cemetery_id: string): Promise<string> {
    try {
      // For now, return a simple response indicating we found relevant information
      // This would typically call an AI service with the context
      if (context.trim()) {
        return `Based on our knowledge base, I found relevant information about your query. Here's what I can tell you: ${context.substring(0, 300)}...`;
      } else {
        return 'I couldn\'t find specific information about that topic in our knowledge base. Please contact our staff for more detailed assistance.';
      }
    } catch (error) {
      console.error('❌ RAG Service: Failed to generate contextual response:', error);
      return 'I apologize, but I\'m having trouble generating a response right now. Please contact our staff directly for assistance.';
    }
  }
  /**
   * Generate embeddings for text content
   * Calls our secure API endpoint for OpenAI embedding generation
   */
  async generateEmbedding(request: EmbeddingRequest): Promise<EmbeddingResponse> {
    try {
      console.log('🔢 RAG Service: Generating embedding for cemetery:', request.cemetery_id);

      const response = await fetch('/api/generate-embedding', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: request.text,
          cemetery_id: request.cemetery_id,
        }),
      });

      if (!response.ok) {
        throw new Error(`Embedding generation failed: ${response.status} ${response.statusText}`);
      }

      const data: EmbeddingResponse = await response.json();
      console.log('✅ RAG Service: Embedding generated successfully');
      
      return data;
    } catch (error) {
      console.error('❌ RAG Service: Embedding generation failed:', error);
      return {
        embedding: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Retrieve knowledge chunks for a specific cemetery
   * Uses caching to improve performance
   */
  async getKnowledgeChunks(cemetery_id: string, useCache = true): Promise<KnowledgeChunk[]> {
    try {
      console.log('📚 RAG Service: Fetching knowledge chunks for cemetery:', cemetery_id);

      // Check cache first
      if (useCache && this.isCacheValid(cemetery_id)) {
        console.log('💾 RAG Service: Using cached knowledge chunks');
        return this.cache.get(cemetery_id) || [];
      }

      if (!isSupabaseAvailable()) {
        console.warn('⚠️ RAG Service: Supabase not available');
        return [];
      }

      const { data, error } = await supabase!
        .from('knowledge_chunks')
        .select('*')
        .eq('cemetery_id', cemetery_id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ RAG Service: Failed to fetch knowledge chunks:', error);
        throw error;
      }

      const chunks = data || [];
      console.log(`✅ RAG Service: Retrieved ${chunks.length} knowledge chunks`);

      // Update cache
      this.cache.set(cemetery_id, chunks);
      this.cacheExpiry.set(cemetery_id, Date.now() + this.CACHE_DURATION);

      return chunks;
    } catch (error) {
      console.error('❌ RAG Service: Error fetching knowledge chunks:', error);
      return [];
    }
  }

  /**
   * Add a new knowledge chunk to the database
   */
  async addKnowledgeChunk(
    cemetery_id: string,
    content: string,
    metadata?: KnowledgeChunk['metadata']
  ): Promise<KnowledgeChunk | null> {
    try {
      console.log('➕ RAG Service: Adding knowledge chunk for cemetery:', cemetery_id);

      if (!isSupabaseAvailable()) {
        console.warn('⚠️ RAG Service: Supabase not available');
        return null;
      }

      // Generate embedding for the content
      const embeddingResponse = await this.generateEmbedding({
        text: content,
        cemetery_id
      });

      if (embeddingResponse.error) {
        console.warn('⚠️ RAG Service: Failed to generate embedding, proceeding without it');
      }

      const { data, error } = await supabase!
        .from('knowledge_chunks')
        .insert({
          cemetery_id,
          content,
          embedding: embeddingResponse.embedding.length > 0 ? embeddingResponse.embedding : null,
          metadata: {
            ...metadata,
            last_updated: new Date().toISOString()
          }
        })
        .select()
        .single();

      if (error) {
        console.error('❌ RAG Service: Failed to add knowledge chunk:', error);
        throw error;
      }

      console.log('✅ RAG Service: Knowledge chunk added successfully');

      // Invalidate cache
      this.invalidateCache(cemetery_id);

      return data;
    } catch (error) {
      console.error('❌ RAG Service: Error adding knowledge chunk:', error);
      return null;
    }
  }

  /**
   * Update an existing knowledge chunk
   */
  async updateKnowledgeChunk(
    id: string,
    cemetery_id: string,
    updates: Partial<Pick<KnowledgeChunk, 'content' | 'metadata'>>
  ): Promise<KnowledgeChunk | null> {
    try {
      console.log('✏️ RAG Service: Updating knowledge chunk:', id);

      if (!isSupabaseAvailable()) {
        console.warn('⚠️ RAG Service: Supabase not available');
        return null;
      }

      const updateData: any = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      // If content is being updated, regenerate embedding
      if (updates.content) {
        const embeddingResponse = await this.generateEmbedding({
          text: updates.content,
          cemetery_id
        });

        if (!embeddingResponse.error && embeddingResponse.embedding.length > 0) {
          updateData.embedding = embeddingResponse.embedding;
        }
      }

      // Update metadata with last_updated timestamp
      if (updates.metadata) {
        updateData.metadata = {
          ...updates.metadata,
          last_updated: new Date().toISOString()
        };
      }

      const { data, error } = await supabase!
        .from('knowledge_chunks')
        .update(updateData)
        .eq('id', id)
        .eq('cemetery_id', cemetery_id)
        .select()
        .single();

      if (error) {
        console.error('❌ RAG Service: Failed to update knowledge chunk:', error);
        throw error;
      }

      console.log('✅ RAG Service: Knowledge chunk updated successfully');

      // Invalidate cache
      this.invalidateCache(cemetery_id);

      return data;
    } catch (error) {
      console.error('❌ RAG Service: Error updating knowledge chunk:', error);
      return null;
    }
  }

  /**
   * Delete a knowledge chunk
   */
  async deleteKnowledgeChunk(id: string, cemetery_id: string): Promise<boolean> {
    try {
      console.log('🗑️ RAG Service: Deleting knowledge chunk:', id);

      if (!isSupabaseAvailable()) {
        console.warn('⚠️ RAG Service: Supabase not available');
        return false;
      }

      const { error } = await supabase!
        .from('knowledge_chunks')
        .delete()
        .eq('id', id)
        .eq('cemetery_id', cemetery_id);

      if (error) {
        console.error('❌ RAG Service: Failed to delete knowledge chunk:', error);
        throw error;
      }

      console.log('✅ RAG Service: Knowledge chunk deleted successfully');

      // Invalidate cache
      this.invalidateCache(cemetery_id);

      return true;
    } catch (error) {
      console.error('❌ RAG Service: Error deleting knowledge chunk:', error);
      return false;
    }
  }

  /**
   * Search knowledge chunks by content (text-based search)
   */
  async searchKnowledgeChunks(
    cemetery_id: string,
    searchTerm: string,
    limit = 10
  ): Promise<KnowledgeChunk[]> {
    try {
      console.log('🔍 RAG Service: Searching knowledge chunks for:', searchTerm);

      if (!isSupabaseAvailable()) {
        console.warn('⚠️ RAG Service: Supabase not available');
        return [];
      }

      const { data, error } = await supabase!
        .from('knowledge_chunks')
        .select('*')
        .eq('cemetery_id', cemetery_id)
        .textSearch('content', searchTerm)
        .limit(limit)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ RAG Service: Failed to search knowledge chunks:', error);
        throw error;
      }

      const results = data || [];
      console.log(`✅ RAG Service: Found ${results.length} matching knowledge chunks`);

      return results;
    } catch (error) {
      console.error('❌ RAG Service: Error searching knowledge chunks:', error);
      return [];
    }
  }

  /**
   * Get knowledge base statistics for a cemetery
   */
  async getKnowledgeBaseStats(cemetery_id: string): Promise<{
    total_chunks: number;
    chunks_with_embeddings: number;
    last_updated: string | null;
    categories: Record<string, number>;
  }> {
    try {
      console.log('📊 RAG Service: Getting knowledge base stats for cemetery:', cemetery_id);

      if (!isSupabaseAvailable()) {
        console.warn('⚠️ RAG Service: Supabase not available');
        return {
          total_chunks: 0,
          chunks_with_embeddings: 0,
          last_updated: null,
          categories: {}
        };
      }

      // Use efficient SQL query to get statistics
      const { data: statsData, error: statsError } = await supabase!
        .rpc('get_knowledge_stats', { cemetery_id_param: cemetery_id });

      if (statsError) {
        console.warn('⚠️ RPC function not available, falling back to manual queries');
        
        // Fallback: Use separate queries
        const [totalResult, embeddingResult, lastUpdatedResult, categoriesResult] = await Promise.all([
          // Total chunks count
          supabase!
            .from('knowledge_chunks')
            .select('id', { count: 'exact', head: true })
            .eq('cemetery_id', cemetery_id),
          
          // Chunks with embeddings count
          supabase!
            .from('knowledge_chunks')
            .select('id', { count: 'exact', head: true })
            .eq('cemetery_id', cemetery_id)
            .not('embedding', 'is', null),
          
          // Last updated timestamp
          supabase!
            .from('knowledge_chunks')
            .select('updated_at')
            .eq('cemetery_id', cemetery_id)
            .order('updated_at', { ascending: false })
            .limit(1)
            .single(),
          
          // Categories breakdown
          supabase!
            .from('knowledge_chunks')
            .select('metadata')
            .eq('cemetery_id', cemetery_id)
        ]);

        // Process results
        const total_chunks = totalResult.count || 0;
        const chunks_with_embeddings = embeddingResult.count || 0;
        const last_updated = lastUpdatedResult.data?.updated_at || null;
        
        // Count categories from metadata
        const categories: Record<string, number> = {};
        if (categoriesResult.data) {
          categoriesResult.data.forEach((row: any) => {
            const category = row.metadata?.category || 'uncategorized';
            categories[category] = (categories[category] || 0) + 1;
          });
        }

        const stats = {
          total_chunks,
          chunks_with_embeddings,
          last_updated,
          categories
        };

        console.log('✅ RAG Service: Knowledge base stats calculated (fallback method)');
        return stats;
      }

      // Use RPC result if available
      const stats = {
        total_chunks: statsData?.total_chunks || 0,
        chunks_with_embeddings: statsData?.chunks_with_embeddings || 0,
        last_updated: statsData?.last_updated || null,
        categories: statsData?.categories || {}
      };

      console.log('✅ RAG Service: Knowledge base stats calculated');
      return stats;
    } catch (error) {
      console.error('❌ RAG Service: Error getting knowledge base stats:', error);
      return {
        total_chunks: 0,
        chunks_with_embeddings: 0,
        last_updated: null,
        categories: {}
      };
    }
  }

  /**
   * Bulk import knowledge chunks from an array
   */
  async bulkImportKnowledgeChunks(
    cemetery_id: string,
    chunks: Array<{
      content: string;
      metadata?: KnowledgeChunk['metadata'];
    }>
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    console.log(`📥 RAG Service: Bulk importing ${chunks.length} knowledge chunks for cemetery:`, cemetery_id);

    const results = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    for (const chunk of chunks) {
      try {
        const result = await this.addKnowledgeChunk(
          cemetery_id,
          chunk.content,
          chunk.metadata
        );

        if (result) {
          results.success++;
        } else {
          results.failed++;
          results.errors.push(`Failed to import chunk: ${chunk.content.substring(0, 50)}...`);
        }
      } catch (error) {
        results.failed++;
        results.errors.push(`Error importing chunk: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    console.log(`✅ RAG Service: Bulk import completed. Success: ${results.success}, Failed: ${results.failed}`);
    return results;
  }

  /**
   * Clear all knowledge chunks for a cemetery (use with caution)
   */
  async clearKnowledgeBase(cemetery_id: string): Promise<boolean> {
    try {
      console.log('🧹 RAG Service: Clearing knowledge base for cemetery:', cemetery_id);

      if (!isSupabaseAvailable()) {
        console.warn('⚠️ RAG Service: Supabase not available');
        return false;
      }

      const { error } = await supabase!
        .from('knowledge_chunks')
        .delete()
        .eq('cemetery_id', cemetery_id);

      if (error) {
        console.error('❌ RAG Service: Failed to clear knowledge base:', error);
        throw error;
      }

      console.log('✅ RAG Service: Knowledge base cleared successfully');

      // Invalidate cache
      this.invalidateCache(cemetery_id);

      return true;
    } catch (error) {
      console.error('❌ RAG Service: Error clearing knowledge base:', error);
      return false;
    }
  }

  /**
   * Check if cache is valid for a cemetery
   */
  private isCacheValid(cemetery_id: string): boolean {
    const expiry = this.cacheExpiry.get(cemetery_id);
    return expiry ? Date.now() < expiry : false;
  }

  /**
   * Invalidate cache for a cemetery
   */
  private invalidateCache(cemetery_id: string): void {
    this.cache.delete(cemetery_id);
    this.cacheExpiry.delete(cemetery_id);
    console.log('🗑️ RAG Service: Cache invalidated for cemetery:', cemetery_id);
  }

  /**
   * Clear all caches
   */
  public clearAllCaches(): void {
    this.cache.clear();
    this.cacheExpiry.clear();
    console.log('🧹 RAG Service: All caches cleared');
  }
}

// Export singleton instance
export const ragService = RAGService.getInstance();

// Export utility functions
export const ragUtils = {
  /**
   * Prepare text content for knowledge base storage
   */
  prepareContent: (text: string): string => {
    return text
      .trim()
      .replace(/\s+/g, ' ') // Normalize whitespace
      .replace(/\n{3,}/g, '\n\n'); // Limit consecutive newlines
  },

  /**
   * Extract metadata from content
   */
  extractMetadata: (content: string, source?: string): KnowledgeChunk['metadata'] => {
    const metadata: KnowledgeChunk['metadata'] = {
      source: source || 'manual',
      last_updated: new Date().toISOString()
    };

    // Detect content type based on keywords
    const lowerContent = content.toLowerCase();
    if (lowerContent.includes('policy') || lowerContent.includes('rule')) {
      metadata.type = 'policy';
    } else if (lowerContent.includes('service') || lowerContent.includes('funeral')) {
      metadata.type = 'service';
    } else if (lowerContent.includes('location') || lowerContent.includes('address')) {
      metadata.type = 'location';
    } else {
      metadata.type = 'general';
    }

    return metadata;
  },

  /**
   * Validate knowledge chunk content
   */
  validateContent: (content: string): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!content || content.trim().length === 0) {
      errors.push('Content cannot be empty');
    }

    if (content.length < 10) {
      errors.push('Content must be at least 10 characters long');
    }

    if (content.length > 8000) {
      errors.push('Content must be less than 8000 characters');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
};